import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSignature,
  Loader2,
  RotateCcw,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SignaturePad, type SignaturePadHandle } from '@/components/audit/SignaturePad';
import { useAchAgreement, useSignAchAgreement } from '@/hooks/useAchAgreement';

/**
 * AchAgreementCard — our payee details as a signed document, for the
 * customer's own vendor file.
 *
 * ⚠ READ THIS BEFORE PROMOTING IT ANYWHERE. This does NOT get around Stripe's
 * bank verification, and it was briefly presented as though it might.
 * Verified against Stripe's docs 2026-09-03: account validation is satisfied
 * by exactly two things — Financial Connections (the bank sign-in) or
 * microdeposits — and Stripe applies that to every ACH integration regardless
 * of how the authorization was obtained. A signed form is an AUTHORIZATION
 * (Stripe accepts offline mandates); authorization and account validation are
 * two different requirements, and this only ever addressed the first. So it
 * cannot replace the 1¢ code, and nothing here should suggest it can.
 *
 * What it IS for, and the only reason it stays: a customer who pays us by
 * their own ACH often has an AP department that needs the payee details on a
 * signed document before it will set us up as a vendor. That is a real,
 * narrow job. It is OPTIONAL and stays out of the way — the details on screen
 * in SelfPayPanel are what a push customer actually needs, and a signature
 * form presented as a step reads as something they must do before they can
 * pay, which is the opposite of the truth.
 *
 * ⚠ Every word of the agreement comes from the server (`agreement`), never
 * from this file. A signature has to attest to text the server produced, and
 * a second copy of legal wording in the client is a copy that drifts.
 *
 * ⚠ It is NOT a debit authorization and says so in three places — the intro,
 * the numbered acknowledgment, and the footnote. Signing it gives us no
 * access to their account.
 */
export function AchAgreementCard({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const { data, isLoading } = useAchAgreement();
  const sign = useSignAchAgreement();

  const [open, setOpen] = useState(defaultOpen);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [drawn, setDrawn] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const padRef = useRef<SignaturePadHandle | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);

  const signed = data?.signed ?? null;
  const agreement = data?.agreement;

  // A deep link (/pay?achform=1) has to land on an OPEN form, or it drops the
  // reader on a collapsed card with nothing explaining why they're here.
  useEffect(() => {
    if (defaultOpen && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);

  useEffect(() => {
    if (open && defaultOpen && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [open, defaultOpen]);

  if (isLoading || !agreement) return null;

  async function submit() {
    setErr(null);
    const signature = padRef.current?.toDataURL() ?? null;
    if (!name.trim() || name.trim().length < 2) {
      setErr('Please type your full name.');
      return;
    }
    if (!signature) {
      setErr('Please sign in the box.');
      return;
    }
    try {
      await sign.mutateAsync({ signature, signed_by: name.trim(), title: title.trim() });
      padRef.current?.clear();
      setDrawn(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "We couldn't save your form. Please try again.");
    }
  }

  const justSigned = sign.data?.signed ?? null;
  const onFile = justSigned ?? signed;

  return (
    <div
      id="ach-setup"
      ref={formRef}
      className="mt-6 rounded-2xl border border-brix-border bg-brix-surface/85 backdrop-blur-xl overflow-hidden"
    >
      <div className="flex items-start gap-3 px-5 py-4 sm:px-6">
        <FileSignature size={20} className="mt-0.5 shrink-0 text-brix-primary" />
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-slate-900">{agreement.headline}</h3>
          <p className="mt-0.5 text-sm text-brix-muted">
            {onFile
              ? `${onFile.signed_by ? `Signed by ${onFile.signed_by}` : 'Signed'} · ${onFile.signed_at_label}`
              : 'Optional. If your AP department needs our payee details on a signed document before it can set us up as a vendor, sign it here and we email you the PDF.'}
          </p>
        </div>
        {onFile && !open && (
          <div className="flex shrink-0 items-center gap-2">
            {onFile.url && (
              <a
                href={onFile.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-brix-border px-3 py-2 text-sm font-semibold text-brix-primary hover:bg-brix-surface"
              >
                <Download size={14} /> PDF
              </a>
            )}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="rounded-lg px-2 py-2 text-sm font-semibold text-brix-muted hover:text-slate-900"
            >
              Sign again
            </button>
          </div>
        )}
        {!onFile && !open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 rounded-lg border border-brix-border px-3 py-2 text-sm font-semibold text-brix-primary hover:bg-brix-surface"
          >
            Open the form
          </button>
        )}
      </div>

      {open && (
        <div className="border-t border-brix-border px-5 py-5 sm:px-6">
          {justSigned && (
            <div className="mb-5 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-bold">Signed — we've emailed you a copy.</p>
                <p className="mt-0.5">
                  Keep it with your vendor records. Our billing desk has been told to expect
                  your payments by bank transfer.
                </p>
                {(sign.data?.notes ?? []).map((n) => (
                  <p key={n} className="mt-1 text-amber-900">
                    {n}
                  </p>
                ))}
                {justSigned.url && (
                  <a
                    href={justSigned.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 font-bold underline"
                  >
                    <Download size={14} /> Download the signed form
                  </a>
                )}
              </div>
            </div>
          )}

          <p className="text-sm leading-relaxed text-slate-700">{agreement.intro}</p>

          <div className="mt-5 space-y-5">
            {agreement.sections.map((s) => (
              <div key={s.heading}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-brix-muted">
                  {s.heading}
                </p>
                <dl className="mt-2 divide-y divide-brix-border/70 rounded-xl border border-brix-border bg-brix-bg/40">
                  {s.fields.map((f) => (
                    <div
                      key={f.label}
                      className="flex flex-col gap-0.5 px-3.5 py-2.5 sm:flex-row sm:items-baseline sm:gap-4"
                    >
                      <dt className="shrink-0 text-sm text-brix-muted sm:w-52">{f.label}</dt>
                      <dd className="break-words font-mono text-sm font-semibold text-slate-900">
                        {f.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <p className="text-[11px] font-bold uppercase tracking-wider text-brix-muted">
              What you're confirming
            </p>
            <ol className="mt-2 space-y-2 rounded-xl border border-brix-border bg-brix-bg/40 px-4 py-3.5">
              {agreement.acknowledgment.map((line, i) => (
                <li key={line} className="flex gap-2.5 text-sm leading-relaxed text-slate-700">
                  <span className="shrink-0 font-bold text-brix-primary">{i + 1}.</span>
                  <span>{line}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="ach-name">Your full name</Label>
              <Input
                id="ach-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Rivera"
                autoComplete="name"
              />
            </div>
            <div>
              <Label htmlFor="ach-title">Title (optional)</Label>
              <Input
                id="ach-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Controller"
                autoComplete="organization-title"
              />
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <Label>Sign here</Label>
              <button
                type="button"
                onClick={() => {
                  padRef.current?.clear();
                  setDrawn(false);
                }}
                className="inline-flex items-center gap-1 text-sm font-semibold text-brix-muted hover:text-slate-900"
              >
                <RotateCcw size={13} /> Clear
              </button>
            </div>
            <div className="mt-1.5 h-40 rounded-xl border-2 border-dashed border-brix-border bg-white">
              <SignaturePad ref={padRef} onDirtyChange={setDrawn} className="h-full w-full" />
            </div>
            <p className="mt-1.5 text-xs text-brix-muted">
              Draw with your finger, stylus or mouse.
            </p>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-brix-muted">{agreement.footnote}</p>

          {err && (
            <p className="mt-3 flex items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              {err}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={submit}
              disabled={sign.isPending || !name.trim() || !drawn}
            >
              {sign.isPending ? (
                <>
                  <Loader2 size={15} className="mr-1.5 animate-spin" /> Saving…
                </>
              ) : (
                'Sign and email me a copy'
              )}
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm font-semibold text-brix-muted hover:text-slate-900"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
